import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const AdClickInput = z.object({
  gclid: z.string().trim().max(200).nullable().optional(),
  utmSource: z.string().trim().max(120).nullable().optional(),
  utmMedium: z.string().trim().max(120).nullable().optional(),
  utmCampaign: z.string().trim().max(200).nullable().optional(),
  utmTerm: z.string().trim().max(200).nullable().optional(),
  landingPage: z.string().trim().max(500).nullable().optional(),
  referrer: z.string().trim().max(500).nullable().optional(),
});

function firstIp(value: string | null) {
  if (!value) return null;
  const ip = value.split(",")[0]?.trim();
  return ip && ip.length <= 60 ? ip : null;
}

/** Registra um acesso vindo de anúncio, capturando o IP real do visitante. */
export const recordAdClick = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => AdClickInput.parse(input))
  .handler(async ({ data }) => {
    const headers = getRequest().headers;
    const ip =
      firstIp(headers.get("cf-connecting-ip")) ??
      firstIp(headers.get("x-real-ip")) ??
      firstIp(headers.get("x-forwarded-for"));

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin.from("ad_clicks").insert({
      ip,
      gclid: data.gclid ?? null,
      utm_source: data.utmSource ?? null,
      utm_medium: data.utmMedium ?? null,
      utm_campaign: data.utmCampaign ?? null,
      utm_term: data.utmTerm ?? null,
      landing_page: data.landingPage ?? null,
      referrer: data.referrer ?? null,
      user_agent: headers.get("user-agent")?.slice(0, 500) ?? null,
      country: headers.get("cf-ipcountry") ?? null,
      city: headers.get("cf-ipcity") ?? null,
    });

    if (error) {
      console.error("Falha ao registrar clique de anúncio:", error.message);
      return { ok: false as const };
    }
    return { ok: true as const };
  });

/** Relatório de cliques de anúncios + IPs reincidentes (possíveis concorrentes). */
export const getAdClicks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Acesso restrito a administradores");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: clicks } = await supabaseAdmin
      .from("ad_clicks")
      .select("id, created_at, ip, gclid, utm_source, utm_campaign, utm_term, landing_page, city, country")
      .order("created_at", { ascending: false })
      .limit(500);

    const rows = clicks ?? [];
    const byIp = new Map<string, { ip: string; count: number; lastAt: string; terms: Set<string> }>();
    for (const row of rows) {
      if (!row.ip) continue;
      const entry = byIp.get(row.ip) ?? { ip: row.ip, count: 0, lastAt: row.created_at, terms: new Set<string>() };
      entry.count += 1;
      if (row.created_at > entry.lastAt) entry.lastAt = row.created_at;
      if (row.utm_term) entry.terms.add(row.utm_term);
      byIp.set(row.ip, entry);
    }

    const suspicious = [...byIp.values()]
      .filter((entry) => entry.count >= 3)
      .sort((a, b) => b.count - a.count)
      .map((entry) => ({ ip: entry.ip, count: entry.count, lastAt: entry.lastAt, terms: [...entry.terms] }));

    return { clicks: rows.slice(0, 150), suspicious, total: rows.length };
  });
